import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useUser } from '@/app/store/auth';
import { User, Edit, Key, Shield } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { SimpleEditProfileDialog } from '../components/SimpleEditProfileDialog';
import { ChangePasswordDialog } from '../components/ChangePasswordDialog';
import { EnhancedAvatar } from '@/components/ui/enhanced-avatar';
import { formatDate } from '@/lib/dateUtils'
import { MyDevices } from '@/features/devices/components/MyDevices';

export function SimpleProfilePage() {
    const { t } = useTranslation();
    const user = useUser();
    const [editProfileOpen, setEditProfileOpen] = useState(false);
    const [changePasswordOpen, setChangePasswordOpen] = useState(false);

    if (!user) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="text-muted-foreground">{t('common.loading')}</div>
            </div>
        );
    }

    return (
        <div className="space-y-4 max-w-4xl">
            {/* Header */}
            <div>
                <h1 className="text-2xl font-bold tracking-tight">{t('profile.settings')}</h1>
                <p className="text-sm text-muted-foreground">
                    {t('profile.settingsDesc')}
                </p>
            </div>

            {/* Profile Information */}
            <Card>
                <CardHeader className="pb-3">
                    <CardTitle className="flex items-center gap-2 text-lg">
                        <User className="h-4 w-4" />
                        {t('profile.profileInfo')}
                    </CardTitle>
                    <CardDescription className="text-sm">
                        {t('profile.profileInfoDesc')}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-3 pt-0">
                    <div className="space-y-2 pb-2">
                        <Label className="text-sm">{t('profile.profilePicture')}</Label>
                        <div className="flex items-center gap-3">
                            <EnhancedAvatar
                                name={user.name}
                                avatarUrl={user.avatarUrl}
                                avatarColor={user.avatarColor}
                                userId={user.id}
                                size="lg"
                            />
                        </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label htmlFor="name" className="text-sm">{t('profile.name')}</Label>
                            <Input
                                id="name"
                                value={user.name}
                                readOnly
                                className="bg-muted h-9 text-sm"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label htmlFor="email" className="text-sm">{t('profile.email')}</Label>
                            <Input
                                id="email"
                                type="email"
                                value={user.email}
                                readOnly
                                className="bg-muted h-9 text-sm"
                            />
                        </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label className="text-sm">{t('profile.status')}</Label>
                            <div className="flex items-center gap-2">
                                <Badge variant={user.isActive ? "success" : "danger"} className="text-xs">
                                    {user.isActive ? t('profile.active') : t('profile.inactive')}
                                </Badge>
                            </div>
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-sm">{t('profile.roles')}</Label>
                            <div className="flex items-center gap-2 flex-wrap">
                                {user.roles?.map((role) => (
                                    <Badge key={role.id} variant="info" className="text-xs">
                                        {role.name}
                                    </Badge>
                                ))}
                            </div>
                        </div>
                    </div>

                    <div className="grid gap-3 md:grid-cols-2">
                        <div className="space-y-1.5">
                            <Label className="text-sm">{t('profile.created')}</Label>
                            <Input
                                value={user.createdAt ? formatDate(user.createdAt) : t('profile.unknown')}
                                readOnly
                                className="bg-muted h-9 text-sm"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-sm">{t('profile.lastUpdated')}</Label>
                            <Input
                                value={user.updatedAt ? formatDate(user.updatedAt) : t('profile.unknown')}
                                readOnly
                                className="bg-muted h-9 text-sm"
                            />
                        </div>
                    </div>
                </CardContent>
            </Card>

            {/* The devices this account signs in with: add a passkey or an authenticator app, remove
                one (never the last). This replaced the TOTP/WebAuthn badges, which read user.mfa - a
                field the server stopped sending when every device became a record - so they showed
                "Disabled" for everyone. */}
            <Card>
                <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                        <Shield className="h-5 w-5" />
                        {t('devices.title')}
                    </CardTitle>
                    <CardDescription>{t('devices.description')}</CardDescription>
                </CardHeader>
                <CardContent>
                    <MyDevices />
                </CardContent>
            </Card>

            {/* Actions */}
            <div className="flex justify-end gap-2">
                <Button
                    variant="outline"
                    onClick={() => setEditProfileOpen(true)}
                >
                    <Edit className="h-4 w-4 mr-2" />
                    {t('profile.editProfile')}
                </Button>
                <Button
                    variant="outline"
                    onClick={() => setChangePasswordOpen(true)}
                >
                    <Key className="h-4 w-4 mr-2" />
                    {t('profile.changePassword')}
                </Button>
            </div>

            {/* Modals */}
            <SimpleEditProfileDialog
                open={editProfileOpen}
                onOpenChange={setEditProfileOpen}
            />
            <ChangePasswordDialog
                open={changePasswordOpen}
                onOpenChange={setChangePasswordOpen}
            />
        </div>
    );
}
